/**
 * Tipi di VISTA della console per la biblioteca unificata (`kb_contents`).
 *
 * ⚠️ I tipi DOCUMENTO non stanno qui: vivono in `shared/kb/kbTypes.ts` e si
 * importano da lì (`KbContenuto`, `KbRevisione`, `KbArea`, `KbGovernance`, …).
 * Riscriverli qui produrrebbe due forme dello stesso documento destinate a
 * divergere — esattamente ciò che il modello condiviso esiste per impedire.
 *
 * Qui stanno solo tre cose che in `shared/` non possono stare:
 *  1. il CONTRATTO delle tre callable (richiesta/risposta): è un contratto di
 *     trasporto fra console e Cloud Function, non la forma di un documento;
 *  2. le forme di appoggio delle tabelle e delle anteprime della console;
 *  3. i tipi degli esiti di lettura che portano con sé la PROVENIENZA del dato
 *     (es. governance letta da Firestore o valori di partenza), perché la UI
 *     deve poterlo dire invece di far finta che sia la stessa cosa.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 * Convenzione campi Firestore: snake_case.
 */

import type { TenantConsoleListItem } from '../services/tenantService';
import type {
  KbContenuto,
  KbFormato,
  KbGovernance,
  KbRevisione,
  KbStato,
  KbStatoRevisione,
  KbTipoModifica,
} from '@shared/kb/kbTypes';

// ============================================================================
// CONTRATTO DELLE CALLABLE (europe-west1)
// ============================================================================

/**
 * `salvaBozzaKbContenuto` — crea o aggiorna la bozza.
 *
 * ⚠️ TIMESTAMP SUL FILO: i campi temporali di `contenuto` viaggiano come
 * MILLISECONDI epoch (`number`), non come `Timestamp`: un Timestamp del client
 * non è serializzabile in una callable. `KbIstanteLike` ammette il numero e
 * `aMillis()` lo legge identico sui due lati, quindi non serve nessuna
 * conversione ad hoc.
 */
export interface SalvaBozzaKbRequest {
  /** Assente = creazione: la CF deriva lo slug dal titolo e ne garantisce l'unicità. */
  content_id?: string;
  contenuto: Partial<KbContenuto>;
  corpo_markdown: string;
}

export interface SalvaBozzaKbResponse {
  content_id: string;
  revisione_bozza: number;
}

/**
 * `pubblicaKbContenuto` — UNICA strada verso lo stato `pubblicato`.
 * Il `motivo` è obbligatorio e resta nel dato (`motivo_ultima_modifica`).
 */
export interface PubblicaKbRequest {
  content_id: string;
  tipo_modifica: KbTipoModifica;
  motivo: string;
}

export interface PubblicaKbResponse {
  content_id: string;
  versione_maggiore: number;
  versione_minore: number;
  revisione_pubblicata: number;
}

export interface ArchiviaKbRequest {
  content_id: string;
}

export interface ArchiviaKbResponse {
  content_id: string;
  stato: 'archiviato';
}

// ============================================================================
// LETTURE
// ============================================================================

/** Revisione letta dalla sottocollezione: il doc id (`'000007'`) serve alla UI. */
export interface KbRevisioneConsole extends KbRevisione {
  id: string;
}

/**
 * Corpo caricato per la modifica o per il confronto maggiore/minore.
 * `null` quando il contenuto non ha ancora nessuna revisione (mai salvato).
 */
export interface KbCorpoCaricato {
  numero: number;
  stato_revisione: KbStatoRevisione;
  titolo_snapshot: string;
  riassunto_snapshot: string;
  corpo_markdown: string;
}

/**
 * Governance con la sua PROVENIENZA.
 *
 * `origine: 'default'` significa che `kb_settings/governance` non esiste ancora:
 * la console usa i valori di partenza del modulo condiviso e lo DICE. Restituire
 * i default facendo finta di aver letto il documento sarebbe il fallback
 * silenzioso che il progetto vieta: l'editore crederebbe di stare configurando
 * un comportamento che invece nessuno ha ancora scritto.
 */
export interface KbGovernanceCaricata {
  governance: KbGovernance;
  origine: 'firestore' | 'default';
}

// ============================================================================
// PLATEA — chi riceverebbe il contenuto
// ============================================================================

/**
 * Utente come serve alla console: per contare i destinatari e per scegliere
 * l'owner di un contenuto.
 *
 * `ruoli` è l'unione di `ruolo` (scalare) e `roles[]` risolta sul tenant
 * PRIMARIO con `risolviRuoloSulTenant` (modulo condiviso): non si re-implementa
 * qui la regola di quale ruolo vale su quale azienda.
 */
export interface KbUtenteConsole {
  uid: string;
  /** Nome leggibile, se c'è; altrimenti l'email; altrimenti l'uid. */
  etichetta: string;
  email: string | null;
  /** Tenant primario (`app_users.azienda_id`). `null` se l'utente non ne ha. */
  tenant_id: string | null;
  ruoli: string[];
  /**
   * L'account può ricevere un annuncio adesso? Falso per gli account
   * disabilitati o sospesi: contarli gonfierebbe il numero di destinatari con
   * persone che non apriranno mai il gestionale.
   */
  operativo: boolean;
}

/** Tenant come serve alla console: si riusa la lettura già esistente. */
export type KbTenantConsole = TenantConsoleListItem;

/** Platea completa, letta UNA volta dalla pagina e passata ai componenti. */
export interface KbPlatea {
  utenti: KbUtenteConsole[];
  tenants: KbTenantConsole[];
}

/**
 * Esito del conteggio destinatari, con i denominatori accanto ai numeratori:
 * «12 persone» non dice nulla, «12 persone su 148, in 3 aziende su 9» sì.
 */
export interface KbAnteprimaDestinatari {
  totale: number;
  per_tenant: Record<string, number>;
  aziende_raggiunte: number;
  aziende_totali: number;
  /** Utenti operativi effettivamente valutati (denominatore delle persone). */
  utenti_valutati: number;
  /** Account esclusi a monte perché disabilitati o sospesi. */
  utenti_non_operativi: number;
}

// ============================================================================
// FILTRI DELLA LISTA
// ============================================================================

/** Valore dei filtri che significa «non filtrare». */
export const KB_FILTRO_TUTTI = 'tutti';

export interface KbFiltriContenuti {
  formato: KbFormato | typeof KB_FILTRO_TUTTI;
  stato: KbStato | typeof KB_FILTRO_TUTTI;
  /** Ricerca libera su titolo, indirizzo e riassunto (in memoria). */
  testo: string;
}

/**
 * Immagine caricata per il corpo markdown: si mostra il percorso Storage
 * (tracciabilità) e si inserisce nel testo l'URL, che è ciò che un renderer
 * markdown qualunque sa risolvere.
 */
export interface KbImmagineCaricata {
  storage_path: string;
  url: string;
  /** Frammento markdown pronto da incollare nel corpo. */
  markdown: string;
}
